/* eslint-disable jira/filename-case */

import {
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
} from '@jira/platform__onboarding-core/src/constants.tsx';
import { defineMessage } from '@jira/platform__react-intl';
import {
	TAILORED_ONBOARDING_SUMMARY,
	TAILORED_ONBOARDING_LIST,
	TAILORED_ONBOARDING_TIMELINE,
	TAILORED_ONBOARDING_PAGES,
	TAILORED_ONBOARDING_FORM,
	TAILORED_ONBOARDING_INVITE,
	TAILORED_ONBOARDING_BOARD,
	TAILORED_ONBOARDING_CALENDAR,
	TAILORED_ONBOARDING_BACKLOG,
	TAILORED_ONBOARDING_CODE,
	JWM_CREATE_PROJECT_TASK,
	JWM_TRELLO_CROSSFLOW_TASK_KEY_DIFFERENCES,
	JWM_BUILD_FORM_TASK,
	JWM_TRELLO_CROSSFLOW_CALENDAR_AND_TIMELINE_TASK,
	JWM_SUMMARY_AND_REPORTS_TASK,
	JWM_TRELLO_CROSSFLOW_TASK_IMPORT,
} from '../../../../common/constants.tsx';
import type { Cards, TaskList } from '../../../../common/types.tsx';
import { DESIGN_VARIANTS } from './design/index.tsx';
import { EVERYTHING_ELSE_VARIANTS } from './everything-else/index.tsx';
import { IT_SUPPORT_VARIANTS } from './it-support/index.tsx';
import { OTHER_PERSONAL_VARIANTS } from './other-personal/index.tsx';
import { PRODUCT_MANAGEMENT_VARIANTS } from './product-management/index.tsx';
import { PROJECT_MANAGEMENT_VARIANTS } from './project-management/index.tsx';
import { TAILORED_ONBOARDING_SINGLE_CONFIG } from './shared-config-new.tsx';
import { SOFTWARE_VARIANTS } from './software/index.tsx';

const rootTitle = defineMessage({
	id: 'software-onboarding-quickstart.services.tasks.config.tailored-onboarding.root-title.no-translate',
	defaultMessage: 'Quickstart',
	description: 'Initial title of the Quickstart',
});

export const TAILORED_ONBOARDING_WITH_SINGLE_CONFIG_TASK_LIST: TaskList = Object.freeze({
	root: {
		title: rootTitle,
		items: [
			TAILORED_ONBOARDING_SUMMARY,
			TAILORED_ONBOARDING_CALENDAR,
			TAILORED_ONBOARDING_LIST,
			TAILORED_ONBOARDING_TIMELINE,
			TAILORED_ONBOARDING_PAGES,
			TAILORED_ONBOARDING_FORM,
			TAILORED_ONBOARDING_INVITE,
			TAILORED_ONBOARDING_BOARD,
			TAILORED_ONBOARDING_BACKLOG,
			TAILORED_ONBOARDING_CODE,
		],
	},
});

export const TAILORED_ONBOARDING_WITH_SINGLE_CONFIG: {
	readonly taskList: TaskList;
	readonly taskVariants: Cards;
} = {
	taskList: TAILORED_ONBOARDING_WITH_SINGLE_CONFIG_TASK_LIST,
	taskVariants: TAILORED_ONBOARDING_SINGLE_CONFIG,
} as const;

// Tailored Onboarding M2P5
export const M2P5_TASK_LIST: TaskList = Object.freeze({
	root: {
		title: rootTitle,
		items: [
			TAILORED_ONBOARDING_SUMMARY,
			TAILORED_ONBOARDING_CALENDAR,
			TAILORED_ONBOARDING_LIST,
			TAILORED_ONBOARDING_TIMELINE,
			TAILORED_ONBOARDING_PAGES,
			TAILORED_ONBOARDING_FORM,
			TAILORED_ONBOARDING_INVITE,
			TAILORED_ONBOARDING_BOARD,
		],
	},
});

// TODO: Remove this and single team type configs on cleanup of to-cleanup-remove-initial-project-template-name
export const getM2P5TaskVariants = (teamType: string): Cards => {
	switch (teamType) {
		case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
			return PRODUCT_MANAGEMENT_VARIANTS;
		case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
			return PROJECT_MANAGEMENT_VARIANTS;
		case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
			return DESIGN_VARIANTS;
		case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
			return IT_SUPPORT_VARIANTS;
		case USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL:
			return OTHER_PERSONAL_VARIANTS;
		case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
			return SOFTWARE_VARIANTS;
		default:
			return EVERYTHING_ELSE_VARIANTS;
	}
};

export const BUSINESS_TASK_LIST: TaskList = Object.freeze({
	root: {
		title: rootTitle,
		items: [
			// JWM Trello Crossflow Tasks
			JWM_CREATE_PROJECT_TASK,
			JWM_TRELLO_CROSSFLOW_TASK_KEY_DIFFERENCES,
			JWM_BUILD_FORM_TASK,
			JWM_TRELLO_CROSSFLOW_CALENDAR_AND_TIMELINE_TASK,
			JWM_SUMMARY_AND_REPORTS_TASK,
			JWM_TRELLO_CROSSFLOW_TASK_IMPORT,
			// JSW Tailored Onboarding Tasks
			TAILORED_ONBOARDING_SUMMARY,
			TAILORED_ONBOARDING_LIST,
			TAILORED_ONBOARDING_TIMELINE,
			TAILORED_ONBOARDING_BOARD,
			TAILORED_ONBOARDING_CALENDAR,
			TAILORED_ONBOARDING_PAGES,
			TAILORED_ONBOARDING_FORM,
			TAILORED_ONBOARDING_INVITE,
		],
	},
});

export const TAILORED_ONBOARDING_M2P5: {
	readonly taskList: TaskList;
	readonly getM2P5TaskVariants: (teamType: string) => Cards;
} = {
	taskList: M2P5_TASK_LIST,
	getM2P5TaskVariants,
} as const;

export const TAILORED_ONBOARDING_BUSINESS: {
	readonly taskList: TaskList;
} = {
	taskList: BUSINESS_TASK_LIST,
} as const;
